import React, { useState, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import toast from "react-hot-toast";
import { useKnowledge } from "../../hooks/useKnowledge";
import KnowledgeFileTabs from "./KnowledgeFileTabs";
import ManualContentTab from "./ManualContentTab";
import UploadDocumentsTab from "./UploadDocumentsTab";
import {
  Plus,
  Search,
  FileText,
  Upload,
  Database,
  X,
  Check,
  AlertTriangle,
  ExternalLink,
  TestTube,
} from "lucide-react";
import DeleteConfirmationModal from "../modals/DeleteConfirmationModal";

const AgentKnowledgeTab = ({
  agentData,
  agentId, // Add agentId prop for explicit ID passing
  knowledgeItems = [], // Keep for backward compatibility but will use hook data
  onKnowledgeUpdate,
  onAddKnowledge,
  onDeleteKnowledge,
}) => {
  // Use explicit agentId prop first, then fallback to agentData.id
  const actualAgentId = agentId || agentData?.id;

  const {
    knowledgeItems: hookKnowledgeItems,
    deleteKnowledge,
    addManualKnowledge,
    updateManualKnowledge,
    uploadDocument,
    searchKnowledge,
    isLoadingKnowledge,
    isDeletingKnowledge,
    isUpdatingKnowledge,
    isSearchingKnowledge,
  } = useKnowledge(actualAgentId);

  // Use hook data if available, fallback to props
  const actualKnowledgeItems = hookKnowledgeItems?.length
    ? hookKnowledgeItems
    : knowledgeItems;

  // State management
  const [activeSubTab, setActiveSubTab] = useState("manual");
  const [editingItem, setEditingItem] = useState(null);
  const [knowledgeQuery, setKnowledgeQuery] = useState("");
  const [knowledgeTestResult, setKnowledgeTestResult] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Modal state for delete confirmation
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Debug logging (after state declaration)
  console.log("🧠 AgentKnowledgeTab data:", {
    agentId: actualAgentId,
    hookKnowledgeItems: hookKnowledgeItems?.length || 0,
    propsKnowledgeItems: knowledgeItems?.length || 0,
    actualKnowledgeItems: actualKnowledgeItems?.length || 0,
    activeSubTab,
  });

  // Add ref for ManualContentTab
  const manualContentTabRef = useRef(null);

  const subTabs = [
    {
      id: "manual",
      name: "Manual Content",
      icon: FileText,
      color: "blue",
    },
    {
      id: "upload",
      name: "Upload Documents",
      icon: Upload,
      color: "green",
    },
    {
      id: "test",
      name: "Test Knowledge",
      icon: TestTube,
      color: "purple",
    },
  ];

  // Filtered items based on search term and active sub-tab
  const filteredItems = (actualKnowledgeItems || []).filter((item) => {
    const matchesSearch =
      !searchTerm ||
      item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.content?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.filename?.toLowerCase().includes(searchTerm.toLowerCase());

    // Additional filtering based on sub-tab if needed
    if (activeSubTab === "upload") {
      // Include all document types (same as KnowledgeFileTabs filter)
      return (
        matchesSearch &&
        (item.type === "document" ||
          item.type === "file" ||
          item.type === "upload" ||
          item.type === "pdf" ||
          item.type === "docx" ||
          item.type === "txt" ||
          item.type === "doc")
      );
    }

    // For manual tab, only show manual items
    if (activeSubTab === "manual") {
      return matchesSearch && (item.type === "manual" || item.type === "text");
    }

    return matchesSearch;
  });

  // Debug filtered items
  console.log("🔍 Filtered items:", {
    searchTerm,
    activeSubTab,
    totalItems: actualKnowledgeItems?.length || 0,
    filteredCount: filteredItems?.length || 0,
    filteredItems: filteredItems?.map((item) => ({
      id: item.id,
      title: item.title,
      type: item.type,
    })),
  });

  const handleEditClick = (item) => {
    setEditingItem(item);
    // Switch to manual tab when editing
    setActiveSubTab("manual");
  };

  const handleDeleteClick = async (item) => {
    // Use modal instead of window.confirm
    setItemToDelete(item);
    setShowDeleteModal(true);
  };

  // Modal handlers
  const handleDeleteModalConfirm = async () => {
    if (itemToDelete) {
      await handleDeleteConfirm(itemToDelete.id);
      setShowDeleteModal(false);
      setItemToDelete(null);
    }
  };

  const handleDeleteModalCancel = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
  };

  const handleDeleteConfirm = async (knowledgeId) => {
    try {
      await deleteKnowledge(knowledgeId);
      if (onDeleteKnowledge) {
        onDeleteKnowledge(knowledgeId);
      }
      toast.success("Knowledge deleted successfully");
    } catch (error) {
      // Error is handled by the hook with toast
      console.error("Error deleting knowledge:", error);
    }
  };

  const handleKnowledgeAdd = async (newKnowledge) => {
    try {
      // Use hook function for API call
      if (newKnowledge.type === "manual") {
        await addManualKnowledge(newKnowledge);
      } else {
        // For documents, this will be handled by UploadDocumentsTab
        console.log("Document knowledge added:", newKnowledge);
      }

      // Call parent callback if provided
      if (onAddKnowledge) {
        onAddKnowledge(newKnowledge);
      }
    } catch (error) {
      console.error("Error adding knowledge:", error);
      throw error;
    }
  };

  const handleKnowledgeUpdate = async (updatedKnowledge) => {
    try {
      // Use the hook mutation instead of direct service call
      await updateManualKnowledge(updatedKnowledge);

      // Reset editing state
      setEditingItem(null);

      // Call parent callback if provided
      if (onKnowledgeUpdate) {
        onKnowledgeUpdate(updatedKnowledge);
      }
    } catch (error) {
      console.error("Error updating knowledge:", error);
      throw error;
    }
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
  };

  const handleTestKnowledge = async (e) => {
    e.preventDefault();

    if (!knowledgeQuery.trim()) {
      return;
    }

    if (!actualAgentId) {
      toast.error("Please save the agent first before testing knowledge");
      return;
    }

    try {
      const result = await searchKnowledge({
        query: knowledgeQuery.trim(),
        limit: 5,
        format: "context",
      });
      setKnowledgeTestResult(result);
    } catch (error) {
      console.error("Error testing knowledge:", error);
      // Error already handled by hook
    }
  };

  const renderSubTabContent = () => {
    switch (activeSubTab) {
      case "manual":
        return (
          <div className="space-y-6">
            {/* Use ManualContentTab for both create and edit */}
            <ManualContentTab
              ref={manualContentTabRef}
              agentData={agentData}
              knowledgeItems={filteredItems}
              onKnowledgeAdd={handleKnowledgeAdd}
              editKnowledge={editingItem}
              onKnowledgeUpdate={handleKnowledgeUpdate}
              onCancelEdit={handleCancelEdit}
              onDeleteKnowledge={handleDeleteConfirm}
            />
          </div>
        );
      case "upload":
        return (
          <div className="space-y-6">
            <UploadDocumentsTab
              agentData={agentData}
              onKnowledgeAdd={handleKnowledgeAdd}
            />
            <KnowledgeFileTabs
              knowledgeItems={filteredItems}
              onDelete={handleDeleteClick}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              filterType="document"
              isLoading={isLoadingKnowledge}
              isDeletingKnowledge={isDeletingKnowledge}
            />
          </div>
        );
      case "test":
        return (
          <div className="space-y-6">
            {/* Test Knowledge Form */}
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Test Knowledge Base
              </h3>
              <form onSubmit={handleTestKnowledge} className="space-y-4">
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={knowledgeQuery}
                    onChange={(e) => setKnowledgeQuery(e.target.value)}
                    placeholder="Ask a question to test your knowledge base..."
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    disabled={isSearchingKnowledge}
                  />
                  <button
                    type="submit"
                    disabled={isSearchingKnowledge || !knowledgeQuery.trim()}
                    className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isSearchingKnowledge ? (
                      <>
                        <svg
                          className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Testing...
                      </>
                    ) : (
                      "Test"
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Test Results */}
            {knowledgeTestResult && !isSearchingKnowledge && (
              <div className="space-y-6">
                {/* Generated Context */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                    Generated Context:
                  </h4>
                  <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap border border-gray-200 dark:border-gray-600">
                    {knowledgeTestResult.context || "No context found"}
                  </div>
                </div>

                {/* Sources */}
                {knowledgeTestResult.sources &&
                  knowledgeTestResult.sources.length > 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                      <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                        Sources:
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {knowledgeTestResult.sources.map((source, index) => (
                          <span
                            key={`${source}-${index}`}
                            className="text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-full"
                          >
                            {source}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                {/* Results */}
                <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                    Results ({knowledgeTestResult.results_count || 0}):
                  </h4>
                  <div className="space-y-4">
                    {knowledgeTestResult.results &&
                    knowledgeTestResult.results.length > 0 ? (
                      knowledgeTestResult.results.map((result, index) => (
                        <div
                          key={index}
                          className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-700"
                        >
                          <p className="text-sm text-gray-800 dark:text-gray-200 mb-3">
                            {result.content}
                          </p>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-1 rounded-full">
                                {result.source}
                              </span>
                              {result.category && (
                                <span className="text-xs font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 px-2 py-1 rounded-full">
                                  {result.category}
                                </span>
                              )}
                              {result.chunk_type && (
                                <span className="text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 px-2 py-1 rounded-full">
                                  {result.chunk_type}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                                Score: {(result.score * 100).toFixed(0)}%
                              </span>
                              <div className="w-20 h-2 bg-gray-200 dark:bg-gray-600 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-green-500 transition-all duration-300"
                                  style={{ width: `${result.score * 100}%` }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <div className="text-gray-400 dark:text-gray-500 mb-2">
                          🔍
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          No results found for your query
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Loading State */}
            {isSearchingKnowledge && (
              <div className="bg-white dark:bg-gray-800 rounded-lg p-8 border border-gray-200 dark:border-gray-700">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Searching knowledge base...
                  </p>
                </div>
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Nested Sub-tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        {/* Sub-tab Navigation */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-8 px-6" aria-label="Knowledge Sub-tabs">
            {subTabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeSubTab === tab.id;

              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveSubTab(tab.id)}
                  className={`
                    flex items-center space-x-2 py-4 border-b-2 font-medium text-sm transition-colors
                    ${
                      isActive
                        ? `border-${tab.color}-500 text-${tab.color}-600 dark:text-${tab.color}-400`
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200"
                    }
                  `}
                  type="button"
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.name}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Sub-tab Content */}
        <div className="p-6">{renderSubTabContent()}</div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleDeleteModalCancel}
        onConfirm={handleDeleteModalConfirm}
        title="Delete Knowledge"
        message={`Are you sure you want to delete "${
          itemToDelete?.title || itemToDelete?.filename || "this item"
        }"? This action cannot be undone.`}
        isLoading={isDeletingKnowledge}
      />
    </div>
  );
};

export default AgentKnowledgeTab;
